// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Search, 
  Calendar, 
  MapPin, 
  Loader2, 
  Save,
  Filter,
  X,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  status: string;
  description?: string;
}

interface EventSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorId: string;
  onEventUpdate?: () => void;
}

const EventSelectionModal: React.FC<EventSelectionModalProps> = ({
  open,
  onOpenChange,
  vendorId,
  onEventUpdate,
}) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [originalSelectedIds, setOriginalSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  useEffect(() => {
    if (open && vendorId) {
      fetchEventsAndAvailability();
    }
  }, [open, vendorId]);

  const fetchEventsAndAvailability = async () => {
    setLoading(true);
    try {
      // Fetch all events
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('id, name, start_date, end_date, venue_name, venue_city, venue_state, status, description')
        .order('start_date', { ascending: true });

      if (eventsError) throw eventsError;

      // Fetch vendor's current availability
      const { data: availabilityData, error: availabilityError } = await supabase
        .from('vendor_event_availability')
        .select('event_id')
        .eq('vendor_id', vendorId);

      if (availabilityError) throw availabilityError;

      setEvents(eventsData || []);
      
      const selectedIds = new Set(availabilityData?.map(item => item.event_id) || []);
      setSelectedEventIds(selectedIds);
      setOriginalSelectedIds(new Set(selectedIds));
    } catch (error) {
      console.error('Error fetching events and availability:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelection = (eventId: string, checked: boolean) => {
    const newSelectedIds = new Set(selectedEventIds);
    
    if (checked) {
      newSelectedIds.add(eventId);
    } else {
      newSelectedIds.delete(eventId);
    }
    
    setSelectedEventIds(newSelectedIds);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allEventIds = new Set(filteredEvents.map(event => event.id));
      setSelectedEventIds(new Set([...selectedEventIds, ...allEventIds]));
    } else {
      const filteredEventIds = new Set(filteredEvents.map(event => event.id));
      const newSelectedIds = new Set([...selectedEventIds].filter(id => !filteredEventIds.has(id)));
      setSelectedEventIds(newSelectedIds);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Delete all existing availability for this vendor
      const { error: deleteError } = await supabase
        .from('vendor_event_availability')
        .delete()
        .eq('vendor_id', vendorId);

      if (deleteError) throw deleteError;

      // Insert new availability records
      if (selectedEventIds.size > 0) {
        const insertData = Array.from(selectedEventIds).map(eventId => ({
          vendor_id: vendorId,
          event_id: eventId,
        }));

        const { error: insertError } = await supabase
          .from('vendor_event_availability')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      toast.success(`Event availability updated successfully! Selected ${selectedEventIds.size} events.`);
      setOriginalSelectedIds(new Set(selectedEventIds));
      onEventUpdate?.();
    } catch (error) {
      console.error('Error saving event availability:', error);
      toast.error('Failed to save event availability');
    } finally {
      setSaving(false);
    }
  };

  const resetSelections = () => {
    setSelectedEventIds(new Set(originalSelectedIds));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStateFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  // Filter events based on search and filter criteria
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.venue_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.venue_city?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesState = !stateFilter || event.venue_state?.toLowerCase().includes(stateFilter.toLowerCase());
    
    const matchesDateFrom = !dateFromFilter || parseLocalDate(event.start_date) >= new Date(dateFromFilter);
    const matchesDateTo = !dateToFilter || parseLocalDate(event.end_date) <= new Date(dateToFilter);

    return matchesSearch && matchesState && matchesDateFrom && matchesDateTo;
  });

  const formatEventDate = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')) {
      return format(start, 'MMM d, yyyy');
    }
    
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'published':
        return 'default';
      case 'draft':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const hasUnsavedChanges = () => {
    return selectedEventIds.size !== originalSelectedIds.size ||
           ![...selectedEventIds].every(id => originalSelectedIds.has(id));
  };

  const uniqueStates = [...new Set(events.map(event => event.venue_state).filter(Boolean))].sort();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Event Selection</DialogTitle>
          <DialogDescription>
            Select the events where you want to offer your video services
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filters
                </CardTitle>
                {(searchTerm || stateFilter || dateFromFilter || dateToFilter) && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search */}
                <div className="space-y-2">
                  <Label htmlFor="search">Search Events</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Event name, venue..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* State Filter */}
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    placeholder="Filter by state..."
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    list="states"
                  />
                  <datalist id="states">
                    {uniqueStates.map(state => (
                      <option key={state} value={state} />
                    ))}
                  </datalist>
                </div>

                {/* Date From */}
                <div className="space-y-2">
                  <Label htmlFor="dateFrom">From Date</Label>
                  <Input
                    id="dateFrom"
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                  />
                </div>

                {/* Date To */}
                <div className="space-y-2">
                  <Label htmlFor="dateTo">To Date</Label>
                  <Input
                    id="dateTo"
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selection Summary */}
          <div className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium">
                  {selectedEventIds.size} events selected
                </span>
              </div>
              <Separator orientation="vertical" className="h-4" />
              <span className="text-sm text-muted-foreground">
                {filteredEvents.length} events shown
              </span>
            </div>
            
            {filteredEvents.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="selectAll"
                  checked={filteredEvents.every(event => selectedEventIds.has(event.id))}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="selectAll" className="text-sm">
                  Select all visible
                </Label>
              </div>
            )}
          </div>

          {/* Events Table */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading events...</span>
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Select</TableHead>
                    <TableHead>Event Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEvents.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedEventIds.has(event.id)}
                          onCheckedChange={(checked) => 
                            handleEventSelection(event.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{event.name}</div>
                          {event.description && (
                            <div className="text-sm text-muted-foreground truncate max-w-[300px]">
                              {event.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {formatEventDate(event.start_date, event.end_date)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {event.venue_name || 'TBD'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {event.venue_city && event.venue_state 
                              ? `${event.venue_city}, ${event.venue_state}`
                              : 'TBD'
                            }
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(event.status)}>
                          {event.status || 'Draft'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-muted-foreground mb-4">
                {events.length === 0 
                  ? 'No events found in the system.'
                  : 'No events match your current filters.'
                }
              </div>
              {(searchTerm || stateFilter || dateFromFilter || dateToFilter) && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <Separator />
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-2">
            {hasUnsavedChanges() && (
              <Button variant="outline" onClick={resetSelections}>
                Reset Changes
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="min-w-[100px]"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Selection
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventSelectionModal;